import React, { Suspense, useMemo } from 'react';
import {
    AnalyticsSenderProvider,
    useAnalytics,
    useAuthManager,
    usePaymentPlace,
} from '@funtech-int/yango-shared';
import { ErrorBoundary } from '@funtech-int/rival-react';
import { TvChannel } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useInView } from 'react-intersection-observer';

import { getAutoOpenWidgetUrl } from '@/shared/lib/getAutoOpenWidgetUrl';
import { PlusPaymentPlace } from '@/shared/constants/payments';
import { usePaymentPage } from '@/shared/hooks/usePaymentPage';
import { Spinner, SpinnerSize } from '@/shared/components/Spinner';
import { useOnMount } from '@/shared/hooks/useOnMount';
import { useLoginUrl } from '@/shared/hooks/useLoginUrl';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { useOfferWebAnalytics } from '@/shared/hooks/useOfferWebAnalytics';

import { transformOfferText } from '../../lib/utils/transformOfferText';
import { useI18n } from '../../hooks/useI18n';
import { createAnalyticsEvents } from '../../lib/createAnalyticsEvents';
import { ChannelWallpaperManager } from '../../lib/channelWallpaperManager';

import { ChannelCardModalComponent } from './ChannelCardModalComponent';
import styles from './ChannelCardModal.module.css';

interface ChannelCardModalContainerBaseProps {
    channel: TvChannel;
    onCloseAfterSuccessPayment?: () => void;
    wallpapperChannelsManager: ChannelWallpaperManager;
}

const ChannelCardModalContainerBase = ({
    onCloseAfterSuccessPayment,
    wallpapperChannelsManager,
    channel,
}: ChannelCardModalContainerBaseProps) => {
    const i18n = useI18n();
    const { isAuthorized } = useAuthManager();

    const page = usePaymentPage();
    const paymentPlace = usePaymentPlace({
        page,
        places: [PlusPaymentPlace.WatchButton],
        extraParams: {
            customProperties: channel.viewOption?.purchaseOptionCustomProperties,
        },
    });

    const offer = paymentPlace.selectOfferBy({
        place: PlusPaymentPlace.WatchButton,
    });

    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();

    const analytics = useAnalytics(createAnalyticsEvents);

    const offerAnalytics = useMemo(
        () =>
            analytics.createOfferAnalyticsEvents(
                offer,
                i18n('channels_offer_button_text'),
                channel?.title.toString(),
                channel.contentId ?? ''
            ),
        [analytics, channel, i18n, offer]
    );

    const offerWebAnalytics = useOfferWebAnalytics(offer);

    const offerLoginUrl = useLoginUrl(getAutoOpenWidgetUrl(offer));

    const controller = useMemo(() => {
        function onPurchaseSuccess() {
            subscriptionRefetch();
            offerWebAnalytics.purchaseSucceeded();

            if (offer) {
                offerAnalytics.succeeded();
            }
        }

        function onSuccessClose() {
            if (!offer) return;

            onCloseAfterSuccessPayment?.();
        }

        return {
            onOfferShow: async () => {
                if (!offer) return;

                offerWebAnalytics.offerShowed();
                paymentPlace?.analytics.offerShowed(offer);
                offerAnalytics.showed();
            },
            onOfferClick: async () => {
                if (!offer) return;

                paymentPlace?.analytics.offerClicked(offer);
                offerAnalytics.navigated();
                offerWebAnalytics.offerClicked();

                await paymentPlace?.checkout(offer, {
                    onPurchaseSuccess,
                    onSuccessClose,
                    onNeedAuth: () => window.location.assign(offerLoginUrl),
                });
            },
        };
    }, [
        offer,
        offerAnalytics,
        offerLoginUrl,
        offerWebAnalytics,
        onCloseAfterSuccessPayment,
        paymentPlace,
        subscriptionRefetch,
    ]);

    useOnMount(() => analytics.channelShowed());

    const { ref: inViewRef } = useInView({
        triggerOnce: true,
        onChange(inView: boolean) {
            if (inView) {
                controller.onOfferShow();
            }
        },
        threshold: 0.8,
    });

    return (
        <ChannelCardModalComponent
            currentChannelWallpaper={{
                contentId: channel?.contentId ?? '',
                title: channel?.title ?? '',
                logo: channel?.logo?.toString() ?? null,
            }}
            offerInfoRef={inViewRef}
            title={i18n('channels_card_modal_title')}
            subscriptionName={offer?.offerTexts?.subscriptionName}
            offerText={transformOfferText({
                offerText: offer?.offerTexts?.buttonText,
                additionalText: offer?.offerTexts?.buttonTextWithDetails,
            })}
            buttonText={
                isAuthorized ? i18n('channels_modal_play_button') : i18n('channels_login_button')
            }
            onBuyButtonClick={controller.onOfferClick}
            isOfferAvailable={!!offer}
            wallpapperChannelsManager={wallpapperChannelsManager}
            isLoginwallEnabled={!isAuthorized}
        />
    );
};

interface ChannelCardModalContainerProps {
    channel: TvChannel;
    wallpapperChannelsManager: ChannelWallpaperManager;
    onCloseAfterSuccessPayment: () => void;
}

const ChannelCardModalContainer = ({
    channel,
    wallpapperChannelsManager,
    onCloseAfterSuccessPayment,
}: ChannelCardModalContainerProps) => (
    <AnalyticsSenderProvider>
        <ErrorBoundary fallback={() => null}>
            <Suspense fallback={<Spinner size={SpinnerSize.BIG} className={styles.spinner} />}>
                <ChannelCardModalContainerBase
                    key={channel.contentId}
                    channel={channel}
                    wallpapperChannelsManager={wallpapperChannelsManager}
                    onCloseAfterSuccessPayment={onCloseAfterSuccessPayment}
                />
            </Suspense>
        </ErrorBoundary>
    </AnalyticsSenderProvider>
);

export default ChannelCardModalContainer;
